import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SubtitlesComponent } from './../../components/subtitles/subtitles.component';
import { Component, ViewChild, AfterViewInit, ChangeDetectorRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SpeechComponent } from '../../components/speech/speech.component';
import { Product, ProductsComponent } from '../../components/products/products.component';
import { ApiService } from '../../service/api.service';
import { FlowRecommendationResponse, TodoItem, CategoryFilter } from '../../service/api.dto';
import { RecommendationsFlowService } from '../services/recommendations-flow.service';
import { SelectedProductsService, SelectedProductEntry } from '../services/selected-products.service';
import { Subscription } from 'rxjs';

const SEARCH_SERVER_ERROR_MESSAGE = 'Lo sentimos, no podemos resolver la búsqueda en este momento. Por favor, Intentalo de nuevo en unos momentos.';

@Component({
  selector: 'app-step2-search',
  standalone: true,
  imports: [CommonModule, FormsModule, SpeechComponent, SubtitlesComponent, ProductsComponent],
  templateUrl: './step2-search.component.html',
  styleUrl: './step2-search.component.scss'
})
export class Step2SearchComponent implements AfterViewInit, OnDestroy {

  products: Product[] = [];
  todoList: TodoItem[] = [];
  activeTodoId: string | null = null;
  categories: CategoryFilter[] = [];
  detailInput: string = '';
  waitingForDetail: boolean = false;
  currentDetailTodoId: string | null = null;
  selectedProducts: SelectedProductEntry[] = [];
  isLoading: boolean = false;
  private selectionsSub?: Subscription;
  private stageCompletionEmitted: boolean = false;
  speechActionButton: 'waiting' | 'listening' | 'loading' = 'listening';

  @ViewChild('speechComponent') speechComponent!: SpeechComponent;
  @ViewChild('subtitles') SubtitlesComponent!: SubtitlesComponent;
  @ViewChild('productComponent', { static: false }) productComponent!: ProductsComponent;
  @Output() error = new EventEmitter<string>();

  @Output() stageCompleted = new EventEmitter<void>();
  @Output() productSelected = new EventEmitter<Product>();

  constructor(
    private apiService: ApiService, 
    private cdr: ChangeDetectorRef,
    private recoFlow: RecommendationsFlowService,
    private selectionService: SelectedProductsService
  ) {
    this.selectionService.clear();
    this.selectionsSub = this.selectionService.selections$.subscribe(entries => {
      this.selectedProducts = entries;
      this.cdr.detectChanges();
    });
  }

  private log(message: string, data?: any) {
    const prefix = '[Step2Search]';
    if (data !== undefined) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }

  private notifyStageCompletion() {
    if (!this.apiService.integratedRecommendationsFlow || this.stageCompletionEmitted) {
      return;
    }
    this.stageCompletionEmitted = true;
    this.stageCompleted.emit();
  }

  /**
   * Determina el mensaje a mostrar según result_text_type
   * Si es "custom", usa result_text del POS
   * Si es "ai_generated", usa el mensaje del backend
   */
  private getResultMessage(backendMessage?: string, fallback?: string): string {
    const posDetails = this.apiService.details;
    if (!posDetails) {
      return backendMessage || fallback || '';
    }

    // Si result_text_type es "custom", usar result_text
    if (posDetails.result_text_type === 'custom') {
      return posDetails.result_text || fallback || '';
    }

    // Si es "ai_generated" o cualquier otro valor, usar el mensaje del backend
    return backendMessage || fallback || '';
  }

  ngAfterViewInit(): void {
    this.speechComponent.startRecording();
    this.SubtitlesComponent.render('Qué buscabas?');
  }

  ngOnDestroy(): void {
    this.selectionsSub?.unsubscribe();
  }

  makeSearch(query: string) {
    // Cambiar el estado del botón a loading INMEDIATAMENTE
    // Esto debe ser lo primero que hacemos para que Angular lo procese
    this.speechActionButton = 'loading';
    
    // Validar si hay texto para enviar
    if (!query || !query.trim()) {
      // No hay texto detectado, cerrar y mostrar mensaje
      this.speechActionButton = 'waiting';
      this.speechComponent.setIdleState();
      this.cdr.detectChanges();
      return;
    }

    // Si estamos esperando un detalle y el usuario envía texto, lo tratamos como detalle
    if (this.waitingForDetail && this.currentDetailTodoId && query.trim()) {
      this.sendDetailResponse(query.trim());
      return;
    }

    // Si hay un activeTodo con opciones presentadas (awaiting_confirmation) y el usuario quiere refinar
    const activeTodo = this.activeTodoId 
      ? this.todoList.find(todo => todo.id === this.activeTodoId)
      : null;

    if (activeTodo && activeTodo.status === 'awaiting_confirmation' && this.products.length > 0 && query.trim()) {
      // El usuario quiere refinar la búsqueda sobre las opciones presentadas
      this.sendFilterDetail(query.trim(), activeTodo.id);
      return;
    }

    this.SubtitlesComponent.render('');
    this.products = [];
    this.waitingForDetail = false;
    this.currentDetailTodoId = null;
    this.isLoading = true;

    // Resetear estado de recomendaciones
    this.recoFlow.resetState();
    
    this.log('makeSearch iniciado', { query });
    this.apiService.makeFirstSearchV2(
      query, 
      this.apiService.details!.catalog.table_name, 
      this.apiService.details!.catalog.id, 
      this.apiService.details!.id
    ).subscribe({
      next: (data: FlowRecommendationResponse) => {
        this.log('Respuesta flujo recomendación inicial', data);
        this.isLoading = false;
        if(!data) {
          this.error.emit('Ocurrió un error en el servidor');
          return;
        }

        // Guardar categorías si vienen en la recomendación
        if (data.recommendations.categories && data.recommendations.categories.length > 0) {
          // Normalizar explícitamente para asegurarnos de tener field y values
          this.categories = data.recommendations.categories.map((c: any) => ({
            field: c.field,
            values: Array.isArray(c.values) ? c.values : []
          }));
        } else {
          this.categories = [];
        }

      // Actualizar todoList si viene en la respuesta
      if (data.recommendations.todoList && data.recommendations.todoList.length > 0) {
        this.apiService.integratedRecommendationsFlow = true;
        this.todoList = data.recommendations.todoList;
      } else if (this.todoList.length === 0) {
        this.apiService.integratedRecommendationsFlow = false;
      }

      // Actualizar activeTodoId
      this.activeTodoId = data.recommendations.activeTodoId || null;

      // Obtener el activeTodo actual
      const activeTodo = this.activeTodoId 
        ? this.todoList.find(todo => todo.id === this.activeTodoId)
        : null;

      // Manejar según el estado del activeTodo
      if (activeTodo) {
        if (activeTodo.status === 'awaiting_confirmation') {
          // Usar productos precargados del todoItem en lugar de hacer búsqueda adicional
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Mostrar productos para selección
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || this.getResultMessage(
              data.recommendations.message,
              productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos'
            );
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((product: any) => ({
              load: false, 
              productID: product.id, 
              name: product.name, 
              price: product.price, 
              image: product.photourl, 
              description: product.search_aggregate
            } as Product));
            this.log('Renderizando productos awaiting_confirmation', this.products);
          } else {
            // No hay productos precargados
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || this.getResultMessage(data.recommendations.message, 'No encontré productos para esta opción');
            this.SubtitlesComponent.render(message);
            this.products = [];
          }
        } else if (activeTodo.status === 'needs_detail') {
          // Verificar si hay productos precargados disponibles
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Hay productos precargados, mostrarlos aunque el status sea needs_detail
            const message = this.getResultMessage(
              data.recommendations.message,
              productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos'
            );
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((product: any) => ({
              load: false, 
              productID: product.id, 
              name: product.name, 
              price: product.price, 
              image: product.photourl, 
              description: product.search_aggregate
            } as Product));
            this.log('Renderizando productos needs_detail con preloadedProducts', this.products);
            this.waitingForDetail = false;
            this.currentDetailTodoId = null;
          } else {
            // No hay productos precargados, necesita más detalles del usuario
            this.waitingForDetail = true;
            this.currentDetailTodoId = activeTodo.id;
            const feedback = this.getResultMessage(data.recommendations.message, '¿Podés darme más detalles?');
            this.SubtitlesComponent.render(feedback);
            this.products = [];
          }
        } else if (activeTodo.status === 'pending') {
          // Aún está buscando
          
          const message = this.getResultMessage(data.recommendations.message, 'Buscando productos...');
          this.SubtitlesComponent.render(message);
          this.products = [];
        }
      } else {
        // No hay activeTodo, todos están completos o no hay items
        if (data.recommendations.products?.length) {
          // Caso legacy: productos sin todoList
          const message = this.getResultMessage(
            data.recommendations.message,
            data.recommendations.products.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos'
          );
          this.SubtitlesComponent.render(message);
          this.products = data.recommendations.products.map((product: any) => ({
            load: false, 
            productID: product.id, 
            name: product.name, 
            price: product.price, 
            image: product.photourl, 
            description: product.search_aggregate
          } as Product));
          this.log('Renderizando productos sin activeTodo', this.products);
        } else {
          // Mostrar mensaje de feedback
          const feedback = this.getResultMessage(data.recommendations.message, 'No encontré productos');
          this.SubtitlesComponent.render(feedback);
          
          if (data.recommendations.search) {
            this.apiService.resetConversation();
          }
        }
      }

      // Cambiar el estado del botón a waiting cuando termine la búsqueda
      this.speechActionButton = 'waiting';
      this.speechComponent.setIdleState();
      this.cdr.detectChanges();

      if (this.productComponent && this.products.length > 0) {
        this.productComponent.loadProducts();
        // Asegurar que Splide se actualice después de que Angular renderice los productos
        setTimeout(() => {
          if (this.productComponent) {
            this.productComponent.refreshSplide();
          }
        }, 200);
      }
      },
      error: (error) => {
        this.log('Error en makeSearch', { error });
        this.isLoading = false;
        if (error?.status === 500) {
          this.SubtitlesComponent.render(SEARCH_SERVER_ERROR_MESSAGE);
          this.products = [];
          this.speechActionButton = 'waiting';
          this.speechComponent.setIdleState();
          this.cdr.detectChanges();
        } else {
          this.error.emit('Ocurrió un error en el servidor');
        }
      }
    });
  }

  onProductSelected(product: Product) {
    this.log('onProductSelected', { productId: product.productID });

    // Buscar el activeTodo que está esperando confirmación
    const activeTodo = this.activeTodoId 
      ? this.todoList.find(todo => todo.id === this.activeTodoId)
      : null;

    if (activeTodo && activeTodo.status === 'awaiting_confirmation') {
      // Guardar/actualizar el producto seleccionado para este todo
      const quantity = activeTodo.quantity || 1;
      this.selectionService.upsertSelection(activeTodo.id, product, 'todo', quantity);

      // Marcar el todo actual como "done" localmente
      activeTodo.status = 'done';
      activeTodo.selectedProductId = product.productID;

      const recommendationsEnabled = this.hasRecommendationsEnabled();

      // Si las recomendaciones están habilitadas, iniciar búsqueda de upselling y cross-selling en segundo plano
      if (recommendationsEnabled) {
        this.apiService.integratedRecommendationsFlow = true;
        this.recoFlow.queueRecommendations(activeTodo.id, activeTodo.name, product, this.categories);
      }

      // Buscar el siguiente todo que NO esté "done" y tenga productos precargados
      const currentIndex = this.todoList.findIndex(todo => todo.id === activeTodo.id);
      const nextTodo = this.todoList.find((todo, index) => 
        index > currentIndex && 
        todo.status !== 'done' && 
        todo.preloadedProducts && 
        todo.preloadedProducts.length > 0
      );

      // Si encontramos un siguiente todo, marcarlo como awaiting_confirmation
      if (nextTodo) {
        nextTodo.status = 'awaiting_confirmation';
        this.activeTodoId = nextTodo.id;

        // Mostrar sus productos precargados inmediatamente
        this.products = nextTodo.preloadedProducts!.map((p: any) => ({
          load: false, 
          productID: p.id, 
          name: p.name, 
          price: p.price, 
          image: p.photourl, 
          description: p.search_aggregate
        } as Product));
        
        this.log('Renderizando siguiente item precargado', { nextTodo: nextTodo.id, products: this.products });
        
        // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje genérico
        if (nextTodo.userFeedback) {
          this.SubtitlesComponent.render(nextTodo.userFeedback);
        } else if(nextTodo.preloadedProducts!.length > 1) {
          this.SubtitlesComponent.render(`Perfecto, agregué ${product.name}. Seleccioná una opción para ${nextTodo.name}.`);
        } else {
          this.SubtitlesComponent.render(`Perfecto, agregué ${product.name}. ¿Querés este ${nextTodo.name}?`);
        }
        
        this.cdr.detectChanges();
        
        if (this.productComponent) {
          this.productComponent.loadProducts();
          setTimeout(() => {
            if (this.productComponent) {
              this.productComponent.refreshSplide();
            }
          }, 200);
        }
      } else {
        // No hay más items con productos precargados
        this.activeTodoId = null;
        this.products = [];
        this.isLoading = true;
        
        // Si las recomendaciones están habilitadas, iniciar el flujo de upselling/cross-selling
      if (recommendationsEnabled) {
        this.SubtitlesComponent.render(`Perfecto, agregué ${product.name}. Buscando recomendaciones...`);
        this.recoFlow.startFlow();
        this.notifyStageCompletion();
      } else {
          this.SubtitlesComponent.render(`Perfecto, agregué ${product.name}. ¿Querés agregar algo más?`);
          this.isLoading = false;
        }
        
        this.cdr.detectChanges();
      }

      // Enviar confirmación al backend en segundo plano (sin bloquear UI)
      this.apiService.makeFirstSearchV2(
        ' ', 
        this.apiService.details!.catalog.table_name, 
        this.apiService.details!.catalog.id, 
        this.apiService.details!.id,
        { todoId: activeTodo.id, productId: product.productID },
        null
      ).subscribe({
        next: (data: FlowRecommendationResponse) => {
          if (!data) {
            this.error.emit('Ocurrió un error al confirmar el producto');
            return;
          }

        this.log('Respuesta backend tras confirmar producto', data);

        // Sincronizar todoList con el backend si es necesario
        if (data.recommendations.todoList) {
          // Solo actualizar items que no hayamos modificado localmente
          data.recommendations.todoList.forEach(backendTodo => {
            const localTodo = this.todoList.find(t => t.id === backendTodo.id);
            if (localTodo && localTodo.status === 'done' && backendTodo.status === 'done') {
              // Ya lo teníamos como done, mantener nuestro estado
              return;
            }
            // Actualizar preloadedProducts si el backend tiene nuevos
            if (localTodo && backendTodo.preloadedProducts) {
              localTodo.preloadedProducts = backendTodo.preloadedProducts;
            }
          });
        }

        // Si el backend necesita que mostremos algo específico (ej: needs_detail)
        // PERO solo si NO estamos ya en el flujo de recomendaciones
        if (!this.recoFlow.isFlowActive() && data.recommendations.activeTodoId !== undefined) {
          const backendActiveTodo = this.todoList.find(todo => todo.id === data.recommendations.activeTodoId);
          
          if (backendActiveTodo && backendActiveTodo.status === 'needs_detail') {
            // Verificar si hay productos precargados disponibles
            const productsToShow = backendActiveTodo.preloadedProducts || data.recommendations.products || [];
            
            this.activeTodoId = data.recommendations.activeTodoId;
            
            if (productsToShow.length > 0) {
              // Hay productos precargados, mostrarlos aunque el status sea needs_detail
              const message = data.recommendations.message || (productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos');
              this.SubtitlesComponent.render(message);
              this.products = productsToShow.map((p: any) => ({
                load: false, 
                productID: p.id, 
                name: p.name, 
                price: p.price, 
                image: p.photourl, 
                description: p.search_aggregate
              } as Product));
              this.log('Renderizando productos needs_detail con preloadedProducts después de confirmar', { todoId: backendActiveTodo.id, products: this.products });
              this.waitingForDetail = false;
              this.currentDetailTodoId = null;
              this.cdr.detectChanges();
              if (this.productComponent) {
                this.productComponent.loadProducts();
                setTimeout(() => {
                  if (this.productComponent) {
                    this.productComponent.refreshSplide();
                  }
                }, 200);
              }
            } else {
              // El backend necesita más detalles, cambiar a ese modo
              this.waitingForDetail = true;
              this.currentDetailTodoId = backendActiveTodo.id;
              this.products = [];
              const feedback = data.recommendations.message || '¿Podés darme más detalles?';
              this.SubtitlesComponent.render(feedback);
              this.cdr.detectChanges();
            }
          }
        }

        this.cdr.detectChanges();
        },
        error: (error) => {
          this.log('Error al confirmar producto', { error });
          // Esta es una llamada en segundo plano, pero si es 500 deberíamos informar
          if (error?.status === 500) {
            // Solo mostrar error si no estamos en el flujo de recomendaciones
            if (!this.recoFlow.isFlowActive()) {
              this.SubtitlesComponent.render(SEARCH_SERVER_ERROR_MESSAGE);
              this.cdr.detectChanges();
            }
          }
        }
      });
    } else {
      // Caso legacy: no hay todoList, emitir directamente
      this.productSelected.emit(product);
    }
  }

  sendFilterDetail(detail: string, todoId: string) {
    if (!detail.trim() || !todoId) {
      return;
    }

    this.SubtitlesComponent.render('');
    this.waitingForDetail = false;
    this.isLoading = true;

    // Enviar filtro adicional usando detailResponse con query vacío
    this.apiService.makeFirstSearchV2(
      ' ', 
      this.apiService.details!.catalog.table_name, 
      this.apiService.details!.catalog.id, 
      this.apiService.details!.id,
      null,
      { todoId, detail: detail.trim() }
    ).subscribe({
      next: (data: FlowRecommendationResponse) => {
        this.isLoading = false;
        if (!data) {
          this.error.emit('Ocurrió un error al filtrar los productos');
          return;
        }

      // Actualizar todoList
      if (data.recommendations.todoList) {
        this.todoList = data.recommendations.todoList;
      }

      this.activeTodoId = data.recommendations.activeTodoId || null;

      const activeTodo = this.activeTodoId 
        ? this.todoList.find(todo => todo.id === this.activeTodoId)
        : null;

      if (activeTodo) {
        if (activeTodo.status === 'awaiting_confirmation') {
          // Usar productos precargados del todoItem después del filtro
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Encontró productos con el nuevo filtro
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || data.recommendations.message || (productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos');
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((p: any) => ({
              load: false, 
              productID: p.id, 
              name: p.name, 
              price: p.price, 
              image: p.photourl, 
              description: p.search_aggregate
            } as Product));
            this.log('Renderizando productos luego de filtro', { todoId: activeTodo.id, products: this.products });
            this.cdr.detectChanges();
            if (this.productComponent) {
              this.productComponent.loadProducts();
              setTimeout(() => {
                if (this.productComponent) {
                  this.productComponent.refreshSplide();
                }
              }, 200);
            }
          } else {
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || data.recommendations.message || 'No encontré productos con ese filtro';
            this.SubtitlesComponent.render(message);
            this.products = [];
          }
        } else if (activeTodo.status === 'needs_detail') {
          // Verificar si hay productos precargados disponibles
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Hay productos precargados, mostrarlos aunque el status sea needs_detail
            const message = data.recommendations.message || (productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos');
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((p: any) => ({
              load: false, 
              productID: p.id, 
              name: p.name, 
              price: p.price, 
              image: p.photourl, 
              description: p.search_aggregate
            } as Product));
            this.log('Renderizando productos needs_detail con preloadedProducts después de filtro', { todoId: activeTodo.id, products: this.products });
            this.waitingForDetail = false;
            this.currentDetailTodoId = null;
            this.cdr.detectChanges();
            if (this.productComponent) {
              this.productComponent.loadProducts();
              setTimeout(() => {
                if (this.productComponent) {
                  this.productComponent.refreshSplide();
                }
              }, 200);
            }
          } else {
            // No encontró productos con el filtro, necesita más detalles
            this.waitingForDetail = true;
            this.currentDetailTodoId = activeTodo.id;
            const feedback = data.recommendations.message || '¿Podés darme más detalles?';
            this.SubtitlesComponent.render(feedback);
            this.products = [];
          }
        } else {
          this.SubtitlesComponent.render(data.recommendations.message || 'Buscando...');
        }
      }

      this.speechActionButton = 'waiting';
      this.speechComponent.setIdleState();
      this.cdr.detectChanges();
      },
      error: (error) => {
        this.log('Error al filtrar productos', { error });
        this.isLoading = false;
        if (error?.status === 500) {
          this.SubtitlesComponent.render(SEARCH_SERVER_ERROR_MESSAGE);
          this.products = [];
          this.speechActionButton = 'waiting';
          this.speechComponent.setIdleState();
          this.cdr.detectChanges();
        } else {
          this.error.emit('Ocurrió un error al filtrar los productos');
        }
      }
    });
  }

  sendDetailResponse(detail: string) {
    if (!this.currentDetailTodoId || !detail.trim()) {
      return;
    }

    this.SubtitlesComponent.render('');
    this.waitingForDetail = false;
    const todoId = this.currentDetailTodoId;
    this.currentDetailTodoId = null;
    this.isLoading = true;

    this.apiService.makeFirstSearchV2(
      ' ', 
      this.apiService.details!.catalog.table_name, 
      this.apiService.details!.catalog.id, 
      this.apiService.details!.id,
      null,
      { todoId, detail: detail.trim() }
    ).subscribe({
      next: (data: FlowRecommendationResponse) => {
        this.isLoading = false;
        if (!data) {
          this.error.emit('Ocurrió un error al enviar el detalle');
          return;
        }

      // Actualizar todoList
      if (data.recommendations.todoList) {
        this.todoList = data.recommendations.todoList;
      }

      this.activeTodoId = data.recommendations.activeTodoId || null;
      this.detailInput = '';

      const activeTodo = this.activeTodoId 
        ? this.todoList.find(todo => todo.id === this.activeTodoId)
        : null;

      if (activeTodo) {
        if (activeTodo.status === 'awaiting_confirmation') {
          // Usar productos precargados del todoItem después de recibir el detalle
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Encontró productos con el nuevo detalle
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || data.recommendations.message || (productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos');
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((p: any) => ({
              load: false, 
              productID: p.id, 
              name: p.name, 
              price: p.price, 
              image: p.photourl, 
              description: p.search_aggregate
            } as Product));
            this.log('Renderizando productos luego de detalle', { todoId: activeTodo.id, products: this.products });
            this.cdr.detectChanges();
            if (this.productComponent) {
              this.productComponent.loadProducts();
              setTimeout(() => {
                if (this.productComponent) {
                  this.productComponent.refreshSplide();
                }
              }, 200);
            }
          } else {
            // Si el item tiene userFeedback, usarlo; de lo contrario usar el mensaje del backend o fallback
            const message = activeTodo.userFeedback || data.recommendations.message || 'No encontré productos con esos detalles';
            this.SubtitlesComponent.render(message);
            this.products = [];
          }
        } else if (activeTodo.status === 'needs_detail') {
          // Verificar si hay productos precargados disponibles
          const productsToShow = activeTodo.preloadedProducts || data.recommendations.products || [];
          
          if (productsToShow.length > 0) {
            // Hay productos precargados, mostrarlos aunque el status sea needs_detail
            const message = data.recommendations.message || (productsToShow.length > 1 ? 'Encontré estos productos para vos' : 'Encontré este producto para vos');
            this.SubtitlesComponent.render(message);
            this.products = productsToShow.map((p: any) => ({
              load: false, 
              productID: p.id, 
              name: p.name, 
              price: p.price, 
              image: p.photourl, 
              description: p.search_aggregate
            } as Product));
            this.log('Renderizando productos needs_detail con preloadedProducts después de detalle', { todoId: activeTodo.id, products: this.products });
            this.waitingForDetail = false;
            this.currentDetailTodoId = null;
            this.cdr.detectChanges();
            if (this.productComponent) {
              this.productComponent.loadProducts();
              setTimeout(() => {
                if (this.productComponent) {
                  this.productComponent.refreshSplide();
                }
              }, 200);
            }
          } else {
            // Aún necesita más detalles
            this.waitingForDetail = true;
            this.currentDetailTodoId = activeTodo.id;
            const feedback = data.recommendations.message || '¿Podés darme más detalles?';
            this.SubtitlesComponent.render(feedback);
            this.products = [];
          }
        } else {
          this.SubtitlesComponent.render(data.recommendations.message || 'Buscando...');
        }
      }

      this.speechActionButton = 'waiting';
      this.speechComponent.setIdleState();
      this.cdr.detectChanges();
      },
      error: (error) => {
        this.log('Error al enviar detalle', { error });
        this.isLoading = false;
        if (error?.status === 500) {
          this.SubtitlesComponent.render(SEARCH_SERVER_ERROR_MESSAGE);
          this.products = [];
          this.speechActionButton = 'waiting';
          this.speechComponent.setIdleState();
          this.cdr.detectChanges();
        } else {
          this.error.emit('Ocurrió un error al enviar el detalle');
        }
      }
    });
  }

  submitDetail() {
    if (this.detailInput.trim()) {
      this.sendDetailResponse(this.detailInput.trim());
    }
  }

  getActiveTodo(): TodoItem | undefined {
    return this.activeTodoId 
      ? this.todoList.find(todo => todo.id === this.activeTodoId)
      : undefined;
  }

  goToCart() {
    const selections = this.selectionService.getSelectionsSnapshot();
    if (selections.length === 0) {
      return;
    }

    if (this.apiService.integratedRecommendationsFlow) {
      this.notifyStageCompletion();
      return;
    }

    selections.forEach(entry => {
      this.productSelected.emit(entry.product);
    });
    this.selectionService.clear();
  }

  hasPendingItems(): boolean {
    return this.todoList.some(todo => todo.status !== 'done');
  }

  trackByTodoId(index: number, todo: TodoItem): string {
    return todo.id;
  }

  private hasRecommendationsEnabled(): boolean {
    const details = this.apiService.details;
    return !!details?.upselling_enabled || !!details?.cross_selling_enabled;
  }

  get recommendationsEnabled(): boolean {
    return this.hasRecommendationsEnabled();
  }
}
